'use client';

import {
  useEffect,
  useMemo,
  useState,
  createContext,
  useContext,
  ReactNode,
  useRef,
} from 'react';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { datadogRum } from '@datadog/browser-rum';

// LaunchDarkly context interface
export interface LaunchDarklyContext {
  kind: string;
  key: string;
  anonymous: boolean;
  email?: string;
  name?: string;
  custom: {
    environment: string;
    isProduction: boolean;
    host: string;
    timestamp: string;
    userId?: number;
    userGuid?: string;
    organizationId?: number;
    userType?: string;
  };
}

// Context for updating LaunchDarkly user information
interface LaunchDarklyUpdateContext {
  updateUser: (userInfo: {
    userId?: number;
    userEmail?: string;
    userName?: string;
    userGuid?: string;
    organizationId?: number;
    userType?: string;
  }) => void;
  getCurrentContext: () => any;
}

const LaunchDarklyUpdateContext =
  createContext<LaunchDarklyUpdateContext | null>(null);

export const useLaunchDarklyUpdate = () => {
  const context = useContext(LaunchDarklyUpdateContext);
  if (!context) {
    throw new Error(
      'useLaunchDarklyUpdate must be used within LaunchDarklyProvider',
    );
  }
  return context;
};

interface LaunchDarklyProviderProps {
  children: ReactNode;
}

export const LaunchDarklyProvider = ({
  children,
}: LaunchDarklyProviderProps) => {
  const [ldClient, setLdClient] = useState<LDClient.LDClient | null>(null);
  const [userInfo, setUserInfo] = useState<{
    userId?: number;
    userEmail?: string;
    userName?: string;
    userGuid?: string;
    organizationId?: number;
    userType?: string;
  }>({});

  // Use refs to track previous values and prevent unnecessary updates
  const prevUserInfoRef = useRef<typeof userInfo>({});
  const clientInitializedRef = useRef(false);

  // Create initial anonymous context - only once
  const initialContext = useMemo(() => {
    const environment = process.env.NODE_ENV || 'development';
    const isProduction = environment === 'production';

    return {
      kind: 'user',
      key: `anonymous-user-${environment}`,
      anonymous: true,
      custom: {
        environment,
        isProduction,
        host:
          typeof window !== 'undefined' ? window.location.hostname : 'unknown',
        timestamp: new Date().toISOString(),
      },
    };
  }, []);

  // Create user context when user info is available - memoized to prevent recreation
  const userContext = useMemo(() => {
    if (!userInfo.userId && !userInfo.userEmail) {
      return initialContext; // Return anonymous context if no user info
    }

    const environment = process.env.NODE_ENV || 'development';
    const isProduction = environment === 'production';

    return {
      kind: 'user',
      key:
        userInfo.userId?.toString() ||
        userInfo.userEmail ||
        `user-${Date.now()}`,
      anonymous: false,
      email: userInfo.userEmail,
      name: userInfo.userName,
      custom: {
        environment,
        isProduction,
        host:
          typeof window !== 'undefined' ? window.location.hostname : 'unknown',
        timestamp: new Date().toISOString(),
        userId: userInfo.userId,
        userGuid: userInfo.userGuid,
        organizationId: userInfo.organizationId,
        userType: userInfo.userType,
      },
    };
  }, [
    initialContext,
    userInfo.userId,
    userInfo.userEmail,
    userInfo.userName,
    userInfo.userGuid,
    userInfo.organizationId,
    userInfo.userType,
  ]);

  // Initialize LaunchDarkly client - only once
  useEffect(() => {
    if (clientInitializedRef.current) return;

    const client = LDClient.initialize(
      process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID!,
      initialContext,
      {
        // Add configuration to reduce connection spam
        bootstrap: 'localStorage',
        sendEvents: true,
        // Use polling instead of streaming for more stable connections
        useReport: false,
        inspectors: [
          {
            type: 'flag-used',
            name: 'dd-inspector',
            method: (key: string, detail: LDClient.LDEvaluationDetail) => {
              datadogRum.addFeatureFlagEvaluation(key, detail.value);
            },
          },
        ],
      },
    );

    client.waitUntilReady().then(() => {
      setLdClient(client);
      clientInitializedRef.current = true;
    });
  }, [initialContext]);

  // Update user context when user info changes - only when actually different
  useEffect(() => {
    if (!ldClient || !userInfo.userId) return;

    // Check if user info has actually changed
    const prevUserInfo = prevUserInfoRef.current;
    const hasChanged =
      prevUserInfo.userId !== userInfo.userId ||
      prevUserInfo.userEmail !== userInfo.userEmail ||
      prevUserInfo.userName !== userInfo.userName ||
      prevUserInfo.userGuid !== userInfo.userGuid ||
      prevUserInfo.organizationId !== userInfo.organizationId ||
      prevUserInfo.userType !== userInfo.userType;

    if (hasChanged) {
      console.log('[LaunchDarkly] Updating user context:', userInfo);
      ldClient.identify(userContext);
      prevUserInfoRef.current = { ...userInfo };
    }
  }, [ldClient, userContext, userInfo]);

  const updateUser = (newUserInfo: {
    userId?: number;
    userEmail?: string;
    userName?: string;
    userGuid?: string;
    organizationId?: number;
    userType?: string;
  }) => {
    setUserInfo((prev) => ({ ...prev, ...newUserInfo }));
  };

  const getCurrentContext = () => {
    return userContext;
  };

  if (!ldClient) return null;

  return (
    <LaunchDarklyUpdateContext.Provider
      value={{ updateUser, getCurrentContext }}
    >
      <LDProvider
        ldClient={ldClient}
        clientSideID={process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID!}
        context={userContext}
      >
        {children}
      </LDProvider>
    </LaunchDarklyUpdateContext.Provider>
  );
};
